import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://lowkeyx.com';

// Organization Schema for brand identity
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "LOWKEYX",
    "alternateName": "LowKeyX",
    "url": BASE_URL,
    "logo": `${BASE_URL}/logo-email.png`,
    "description": "Plataforma profesional de inteligencia OSINT, ciberseguridad y conciencia digital",
    "foundingDate": "2024",
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": ["Spanish", "English"]
    },
    "areaServed": "Worldwide",
    "knowsAbout": [
      "OSINT",
      "Ciberseguridad",
      "Inteligencia Digital",
      "Hacking Ético",
      "Investigación Digital",
      "Conciencia Digital"
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// WebApplication Schema for the platform
export const WebApplicationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "LOWKEYX",
    "description": "Plataforma profesional de herramientas OSINT para inteligencia digital, ciberseguridad y formación en conciencia digital",
    "url": BASE_URL,
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "Web Browser",
    "browserRequirements": "Requires JavaScript",
    "softwareVersion": "2.0",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    },
    "creator": {
      "@type": "Organization",
      "name": "LOWKEYX"
    },
    "featureList": [
      "Análisis de emails e inteligencia de correo",
      "Reconocimiento de dominios web",
      "Geolocalización y análisis de IPs",
      "Búsqueda de usernames en múltiples plataformas",
      "Detección de filtraciones de datos",
      "Academia de ciberseguridad",
      "Formación en conciencia digital"
    ],
    "screenshot": `${BASE_URL}/og-image.png`
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Breadcrumb Schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Article Schema for learning/research content
interface ArticleSchemaProps {
  title: string;
  description: string;
  url: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
  section?: string;
  keywords?: string[];
  wordCount?: number;
  timeRequired?: string;
}

export const ArticleSchema = ({
  title,
  description,
  url,
  image,
  datePublished,
  dateModified,
  author = 'LOWKEYX',
  section,
  keywords = [],
  wordCount,
  timeRequired,
}: ArticleSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "url": url.startsWith('http') ? url : `${BASE_URL}${url}`,
    "image": image ? (image.startsWith('http') ? image : `${BASE_URL}${image}`) : `${BASE_URL}/og-image.png`,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Organization",
      "name": author,
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "LOWKEYX",
      "logo": {
        "@type": "ImageObject",
        "url": `${BASE_URL}/logo-email.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url.startsWith('http') ? url : `${BASE_URL}${url}`
    },
    ...(section && { "articleSection": section }),
    ...(keywords.length > 0 && { "keywords": keywords.join(', ') }),
    ...(wordCount && { "wordCount": wordCount }),
    ...(timeRequired && { "timeRequired": timeRequired })
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// FAQ Schema
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSchemaProps {
  items: FAQItem[];
}

export const FAQSchema = ({ items }: FAQSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Course/Learning Path Schema
interface CourseSchemaProps {
  name: string;
  description: string;
  url: string;
  provider?: string;
  duration?: string;
  difficulty?: 'Beginner' | 'Intermediate' | 'Advanced';
  image?: string;
}

export const CourseSchema = ({
  name,
  description,
  url,
  provider = 'LOWKEYX',
  duration,
  difficulty,
  image,
}: CourseSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": name,
    "description": description,
    "url": url.startsWith('http') ? url : `${BASE_URL}${url}`,
    "provider": {
      "@type": "Organization",
      "name": provider,
      "url": BASE_URL
    },
    "isAccessibleForFree": true,
    "inLanguage": "es",
    ...(duration && { "timeRequired": duration }),
    ...(difficulty && { 
      "educationalLevel": difficulty,
      "coursePrerequisites": difficulty === 'Beginner' ? 'Ninguno' : 'Conocimientos básicos de informática'
    }),
    ...(image && { "image": image.startsWith('http') ? image : `${BASE_URL}${image}` })
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Software Application Schema for Tools
interface ToolSchemaProps {
  name: string;
  description: string;
  url: string;
  category?: string;
}

export const ToolSchema = ({
  name,
  description,
  url,
  category = 'SecurityApplication',
}: ToolSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": name,
    "description": description,
    "url": url.startsWith('http') ? url : `${BASE_URL}${url}`,
    "applicationCategory": category,
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "creator": {
      "@type": "Organization",
      "name": "LOWKEYX"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// ItemList Schema for lists of articles/tools
interface ItemListSchemaProps {
  name: string;
  description: string;
  items: Array<{
    name: string;
    url: string;
    description?: string;
    image?: string;
  }>;
}

export const ItemListSchema = ({ name, description, items }: ItemListSchemaProps) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": name,
    "description": description,
    "numberOfItems": items.length,
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Thing",
        "name": item.name,
        "url": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
        ...(item.description && { "description": item.description }),
        ...(item.image && { "image": item.image.startsWith('http') ? item.image : `${BASE_URL}${item.image}` })
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// WebSite Schema with SearchAction for sitelinks search box
export const WebSiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "LOWKEYX",
    "alternateName": ["LowKeyX", "LOWKEY X"],
    "url": BASE_URL,
    "description": "Plataforma profesional de inteligencia OSINT, ciberseguridad y conciencia digital",
    "inLanguage": "es",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${BASE_URL}/knowledge?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
